import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';
import './JobDetailsModal.css';

const GARMENT_TYPES = [
  'T-Shirt',
  'Long Sleeve T-Shirt',
  'Hoodie',
  'Sweatshirt',
  'Crewneck',
  'Polo',
  'Tank Top',
  'Jersey',
  'Jacket',
  'Hat/Cap',
  'Bag',
  'Other'
];

const SIZE_COLUMNS = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

const PRIORITY_COLORS = {
  rush: '#dc2626',
  high: '#f59e0b',
  normal: '#3b82f6',
  low: '#6b7280'
};

const STATUS_COLORS = {
  'Order Received': '#6366f1',
  'Art/Design': '#8b5cf6',
  'Screen Setup': '#ec4899',
  'Printing': '#f59e0b',
  'Quality Check': '#10b981',
  'Ready for Pickup': '#22c55e'
};

const EDIT_STEPS = [
  { id: 1, title: 'Customer', icon: '👤' },
  { id: 2, title: 'Garments', icon: '👕' },
  { id: 3, title: 'Details', icon: '📋' },
  { id: 4, title: 'Files', icon: '🎨' }
];

const createEmptySizes = () => ({
  XS: 0, S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0, '4XL': 0, '5XL': 0
});

const createEmptyColor = () => ({
  id: Date.now() + Math.random().toString(36).substr(2, 9),
  color_name: '',
  price_adjustment: '',
  sizes: createEmptySizes()
});

const createEmptyGarment = () => ({
  id: Date.now() + Math.random().toString(36).substr(2, 9),
  garment_type: '',
  base_price: '',
  size_prices: {},
  colors: [createEmptyColor()]
});

// Parse existing order_details to garments array with new structure
const parseOrderDetailsToGarments = (job) => {
  // If order_details has garments array, use it
  if (job.order_details?.garments && Array.isArray(job.order_details.garments)) {
    return job.order_details.garments.map(g => {
      // Check if garment has new colors structure
      if (g.colors && Array.isArray(g.colors)) {
        return {
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          garment_type: g.garment_type || '',
          base_price: g.base_price || '',
          size_prices: g.size_prices || {},
          colors: g.colors.map(c => ({
            id: Date.now() + Math.random().toString(36).substr(2, 9),
            color_name: c.color_name || '',
            price_adjustment: c.price_adjustment || '',
            sizes: c.sizes || createEmptySizes()
          }))
        };
      }
      // Old format: single color per garment
      return {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        garment_type: g.garment_type || '',
        base_price: '',
        size_prices: {},
        colors: [{
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          color_name: g.garment_color || '',
          price_adjustment: '',
          sizes: g.sizes || createEmptySizes()
        }]
      };
    });
  }

  // Legacy: single garment from job fields
  if (job.garment_type || job.garment_color || job.quantity > 0) {
    return [{
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      garment_type: job.garment_type || '',
      base_price: '',
      size_prices: {},
      colors: [{
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        color_name: job.garment_color || '',
        price_adjustment: '',
        sizes: {
          ...createEmptySizes(),
          M: job.quantity || 0 // Put legacy quantity in M by default
        }
      }]
    }];
  }

  return [createEmptyGarment()];
};

const JobDetailsModal = ({
  job,
  isOpen,
  onClose,
  onSuccess,
  canAdmin,
  workflowStages
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editStep, setEditStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit form state
  const [customerData, setCustomerData] = useState({});
  const [garments, setGarments] = useState([]);
  const [jobDetails, setJobDetails] = useState({});
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // File state
  const [existingArtFiles, setExistingArtFiles] = useState([]);
  const [newArtFiles, setNewArtFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Other state
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusChangeNote, setStatusChangeNote] = useState('');
  const [errors, setErrors] = useState({});

  // Reset state when job changes
  useEffect(() => {
    if (job) {
      resetEditState();
      fetchHistory();
    }
  }, [job]);

  // Fetch users when editing
  useEffect(() => {
    if (isEditing && canAdmin) {
      fetchUsers();
    }
  }, [isEditing, canAdmin]);

  const resetEditState = () => {
    if (!job) return;

    setCustomerData({
      customer_name: job.customer_name || '',
      school_name: job.school_name || '',
      contact_name: job.contact_name || '',
      contact_phone: job.contact_phone || '',
      contact_email: job.contact_email || ''
    });

    setGarments(parseOrderDetailsToGarments(job));

    setJobDetails({
      job_description: job.job_description || '',
      priority: job.priority || 'normal',
      due_date: job.due_date ? job.due_date.split('T')[0] : '',
      total_price: job.total_price || '',
      assigned_to: job.assigned_to || ''
    });

    setNotes(job.notes || '');
    setInternalNotes(job.internal_notes || '');
    setExistingArtFiles(job.art_files || []);
    setNewArtFiles([]);
    setIsEditing(false);
    setEditStep(1);
    setError(null);
    setErrors({});
  };

  const fetchHistory = async () => {
    if (!job?.id) return;
    try {
      const response = await api.get(`/kerrscreen/jobs/${job.id}/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/kerrscreen/users');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Calculate quantities
  const getColorQuantity = (color) => {
    return Object.values(color.sizes).reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
  };

  const getGarmentQuantity = (garment) => {
    return garment.colors.reduce((total, color) => total + getColorQuantity(color), 0);
  };

  const calculateTotalQuantity = () => {
    return garments.reduce((total, garment) => total + getGarmentQuantity(garment), 0);
  };

  // Calculate total price
  const calculateTotalPrice = () => {
    let total = 0;
    garments.forEach(garment => {
      const basePrice = parseFloat(garment.base_price) || 0;
      garment.colors.forEach(color => {
        const colorAdj = parseFloat(color.price_adjustment) || 0;
        SIZE_COLUMNS.forEach(size => {
          const qty = parseInt(color.sizes[size]) || 0;
          if (qty > 0) {
            const sizePrice = garment.size_prices[size] !== undefined && garment.size_prices[size] !== ''
              ? parseFloat(garment.size_prices[size])
              : basePrice;
            total += qty * (sizePrice + colorAdj);
          }
        });
      });
    });
    return total;
  };

  // Format helpers
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    return '📎';
  };

  // Validation
  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!customerData.customer_name.trim()) {
          newErrors.customer_name = 'Customer name is required';
        }
        break;
      case 2:
        garments.forEach((garment, index) => {
          if (!garment.garment_type) {
            newErrors[`garment_${index}_type`] = 'Select a garment type';
          }
          const qty = getGarmentQuantity(garment);
          if (qty === 0) {
            newErrors[`garment_${index}_sizes`] = 'Enter at least one size quantity';
          }
        });
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Edit navigation
  const goToEditStep = (step) => {
    if (step < editStep || validateStep(editStep)) {
      setEditStep(step);
    }
  };

  const nextEditStep = () => {
    if (validateStep(editStep)) {
      setEditStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevEditStep = () => {
    setEditStep(prev => Math.max(prev - 1, 1));
  };

  // Customer handlers
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'contact_phone') {
      processedValue = formatPhoneNumber(value);
    }

    setCustomerData(prev => ({ ...prev, [name]: processedValue }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Garment handlers
  const addGarment = () => {
    setGarments(prev => [...prev, createEmptyGarment()]);
  };

  const removeGarment = (garmentId) => {
    if (garments.length > 1) {
      setGarments(prev => prev.filter(g => g.id !== garmentId));
    }
  };

  const duplicateGarment = (garment) => {
    const newGarment = {
      ...createEmptyGarment(),
      garment_type: garment.garment_type,
      base_price: garment.base_price || '',
      size_prices: { ...garment.size_prices },
      colors: garment.colors ? garment.colors.map(c => ({
        ...createEmptyColor(),
        color_name: c.color_name || '',
        price_adjustment: c.price_adjustment || '',
        sizes: { ...c.sizes }
      })) : [createEmptyColor()]
    };
    setGarments(prev => [...prev, newGarment]);
  };

  const updateGarment = (garmentId, field, value) => {
    setGarments(prev => prev.map(g =>
      g.id === garmentId ? { ...g, [field]: value } : g
    ));

    const index = garments.findIndex(g => g.id === garmentId);
    if (errors[`garment_${index}_type`] || errors[`garment_${index}_sizes`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`garment_${index}_type`];
        delete newErrors[`garment_${index}_sizes`];
        return newErrors;
      });
    }
  };

  const updateGarmentColor = (garmentId, colorName) => {
    setGarments(prev => prev.map(g => {
      if (g.id !== garmentId) return g;
      const updatedColors = g.colors ? g.colors.map((c, i) =>
        i === 0 ? { ...c, color_name: colorName } : c
      ) : [{ ...createEmptyColor(), color_name: colorName }];
      return { ...g, colors: updatedColors };
    }));
  };

  const updateGarmentSize = (garmentId, size, value) => {
    const qty = Math.max(0, parseInt(value) || 0);
    setGarments(prev => prev.map(g => {
      if (g.id !== garmentId) return g;
      // Update the first color's sizes (edit wizard shows single color per garment)
      const updatedColors = g.colors ? g.colors.map((c, i) =>
        i === 0 ? { ...c, sizes: { ...c.sizes, [size]: qty } } : c
      ) : [{ ...createEmptyColor(), sizes: { [size]: qty } }];
      return { ...g, colors: updatedColors };
    }));

    const index = garments.findIndex(g => g.id === garmentId);
    if (errors[`garment_${index}_sizes`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`garment_${index}_sizes`];
        return newErrors;
      });
    }
  };

  // Job details handlers
  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'total_price') {
      processedValue = value === '' ? '' : parseFloat(value) || '';
    }

    setJobDetails(prev => ({ ...prev, [name]: processedValue }));
  };

  // File handlers
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = '';
  };

  const processFiles = (files) => {
    if (files.length === 0) return;

    const newFiles = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Date.now() + Math.random().toString(36).substr(2, 9)
    }));

    setNewArtFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleRemoveExistingFile = (index) => {
    setExistingArtFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewFile = (fileId) => {
    setNewArtFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Save changes
  const handleSaveEdit = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      setError('Please complete all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      // Customer data
      Object.keys(customerData).forEach(key => {
        if (customerData[key]) {
          formData.append(key, customerData[key]);
        }
      });

      // Total quantity
      formData.append('quantity', calculateTotalQuantity());

      // Job details
      Object.keys(jobDetails).forEach(key => {
        if (jobDetails[key] !== '' && jobDetails[key] !== null) {
          formData.append(key, jobDetails[key]);
        }
      });

      // Notes
      formData.append('notes', notes);
      if (canAdmin) {
        formData.append('internal_notes', internalNotes);
      }

      // Garments as order_details (save with colors array structure)
      const orderDetails = {
        garments: garments.map(g => ({
          garment_type: g.garment_type,
          base_price: g.base_price || '',
          size_prices: g.size_prices || {},
          colors: g.colors ? g.colors.map(c => ({
            color_name: c.color_name || '',
            price_adjustment: c.price_adjustment || '',
            sizes: c.sizes || {}
          })) : [],
          quantity: getGarmentQuantity(g)
        }))
      };
      formData.append('order_details', JSON.stringify(orderDetails));

      // Legacy fields from first garment (for backward compatibility)
      if (garments.length > 0) {
        formData.append('garment_type', garments[0].garment_type);
        formData.append('garment_color', garments[0].colors?.[0]?.color_name || '');
      }

      // Existing files to keep
      formData.append('existing_art_files', JSON.stringify(existingArtFiles));

      // New files
      newArtFiles.forEach(({ file }) => {
        formData.append('art_files', file);
      });

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
      await axios.put(`${baseURL}/kerrscreen/jobs/${job.id}`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setIsEditing(false);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  // Status change
  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/kerrscreen/jobs/${job.id}/status`, {
        status: newStatus,
        notes: statusChangeNote
      });

      setStatusChangeNote('');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  // Delete job
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.delete(`/kerrscreen/jobs/${job.id}`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete job');
      setLoading(false);
    }
  };

  // Archive job
  const handleArchive = async () => {
    if (!window.confirm('Archive this job? Archived jobs can be restored from the Archive view.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.patch(`/kerrscreen/jobs/${job.id}/archive`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to archive job');
      setLoading(false);
    }
  };

  // Unarchive job
  const handleUnarchive = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.patch(`/kerrscreen/jobs/${job.id}/unarchive`);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore job');
      setLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    resetEditState();
  };

  // Start editing
  const handleStartEdit = () => {
    setIsEditing(true);
    setEditStep(1);
  };

  if (!isOpen || !job) return null;

  // Get garments for display from order_details or legacy fields
  const displayGarments = job.order_details?.garments || (
    (job.garment_type || job.garment_color) ? [{
      garment_type: job.garment_type,
      garment_color: job.garment_color,
      sizes: {},
      quantity: job.quantity
    }] : []
  );

  // Render edit step content
  const renderEditStepContent = () => {
    switch (editStep) {
      case 1:
        return (
          <div className="edit-wizard-step">
            <div className="edit-step-header">
              <h3>Customer Information</h3>
            </div>
            <div className="edit-form-grid">
              <div className="edit-form-group full-width">
                <label>Customer Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="customer_name"
                  value={customerData.customer_name}
                  onChange={handleCustomerChange}
                  placeholder="Enter customer name"
                  className={errors.customer_name ? 'input-error' : ''}
                />
                {errors.customer_name && <span className="error-text">{errors.customer_name}</span>}
              </div>
              <div className="edit-form-group full-width">
                <label>School / Organization</label>
                <input
                  type="text"
                  name="school_name"
                  value={customerData.school_name}
                  onChange={handleCustomerChange}
                  placeholder="Enter school or organization"
                />
              </div>
              <div className="edit-form-group">
                <label>Contact Name</label>
                <input
                  type="text"
                  name="contact_name"
                  value={customerData.contact_name}
                  onChange={handleCustomerChange}
                  placeholder="Primary contact"
                />
              </div>
              <div className="edit-form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="contact_phone"
                  value={customerData.contact_phone}
                  onChange={handleCustomerChange}
                  placeholder="123-456-7890"
                />
              </div>
              <div className="edit-form-group full-width">
                <label>Email</label>
                <input
                  type="email"
                  name="contact_email"
                  value={customerData.contact_email}
                  onChange={handleCustomerChange}
                  placeholder="email@example.com"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="edit-wizard-step">
            <div className="edit-step-header">
              <h3>Garments & Sizes</h3>
            </div>
            <div className="edit-garments-container">
              {garments.map((garment, index) => (
                <div key={garment.id} className="edit-garment-card">
                  <div className="edit-garment-header">
                    <span className="garment-number">Garment {index + 1}</span>
                    <div className="garment-actions">
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => duplicateGarment(garment)}
                        title="Duplicate"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                      {garments.length > 1 && (
                        <button
                          type="button"
                          className="btn-icon btn-danger"
                          onClick={() => removeGarment(garment.id)}
                          title="Remove"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="edit-garment-body">
                    <div className="edit-garment-row">
                      <div className="edit-form-group">
                        <label>Garment Type <span className="required">*</span></label>
                        <select
                          value={garment.garment_type}
                          onChange={(e) => updateGarment(garment.id, 'garment_type', e.target.value)}
                          className={errors[`garment_${index}_type`] ? 'input-error' : ''}
                        >
                          <option value="">-- Select --</option>
                          {GARMENT_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {errors[`garment_${index}_type`] && (
                          <span className="error-text">{errors[`garment_${index}_type`]}</span>
                        )}
                      </div>
                      <div className="edit-form-group">
                        <label>Color</label>
                        <input
                          type="text"
                          value={garment.colors?.[0]?.color_name || ''}
                          onChange={(e) => updateGarmentColor(garment.id, e.target.value)}
                          placeholder="e.g., Navy Blue"
                        />
                      </div>
                    </div>
                    <div className="edit-size-grid-container">
                      <label>Size Quantities <span className="required">*</span></label>
                      <div className="edit-size-grid">
                        {SIZE_COLUMNS.map(size => (
                          <div key={size} className="size-cell">
                            <label className="size-label">{size}</label>
                            <input
                              type="number"
                              min="0"
                              value={garment.colors?.[0]?.sizes?.[size] || ''}
                              onChange={(e) => updateGarmentSize(garment.id, size, e.target.value)}
                              placeholder="0"
                              className="size-input"
                            />
                          </div>
                        ))}
                      </div>
                      {errors[`garment_${index}_sizes`] && (
                        <span className="error-text">{errors[`garment_${index}_sizes`]}</span>
                      )}
                      <div className="garment-total">
                        Total: <strong>{getGarmentQuantity(garment)}</strong> pieces
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-add-garment" onClick={addGarment}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Another Garment
              </button>
            </div>
            <div className="edit-garments-summary">
              <span>Total: <strong>{garments.length}</strong> garment(s), <strong>{calculateTotalQuantity()}</strong> pieces</span>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="edit-wizard-step">
            <div className="edit-step-header">
              <h3>Job Details</h3>
            </div>
            <div className="edit-form-grid">
              <div className="edit-form-group full-width">
                <label>Job Description</label>
                <textarea
                  name="job_description"
                  value={jobDetails.job_description}
                  onChange={handleDetailsChange}
                  placeholder="Describe the print job..."
                  rows="3"
                />
              </div>
              <div className="edit-form-group">
                <label>Priority</label>
                <select
                  name="priority"
                  value={jobDetails.priority}
                  onChange={handleDetailsChange}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="rush">Rush</option>
                </select>
              </div>
              <div className="edit-form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  name="due_date"
                  value={jobDetails.due_date}
                  onChange={handleDetailsChange}
                />
              </div>
              <div className="edit-form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  name="total_price"
                  value={jobDetails.total_price}
                  onChange={handleDetailsChange}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
              {canAdmin && (
                <div className="edit-form-group">
                  <label>Assigned To</label>
                  <select
                    name="assigned_to"
                    value={jobDetails.assigned_to}
                    onChange={handleDetailsChange}
                  >
                    <option value="">-- Unassigned --</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="edit-wizard-step">
            <div className="edit-step-header">
              <h3>Files & Notes</h3>
            </div>

            {/* Existing Files */}
            {existingArtFiles.length > 0 && (
              <div className="edit-files-section">
                <label>Current Art Files</label>
                <div className="edit-files-list">
                  {existingArtFiles.map((file, index) => (
                    <div key={index} className="edit-file-item">
                      <span className="file-icon">{getFileIcon(file.mimetype)}</span>
                      <div className="file-info">
                        <span className="file-name">{file.originalName || file.filename}</span>
                        {file.size && <span className="file-size">{formatFileSize(file.size)}</span>}
                      </div>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => handleRemoveExistingFile(index)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Files */}
            {newArtFiles.length > 0 && (
              <div className="edit-files-section">
                <label>New Files to Upload</label>
                <div className="edit-files-list">
                  {newArtFiles.map(file => (
                    <div key={file.id} className="edit-file-item new-file">
                      <span className="file-icon">{getFileIcon(file.type)}</span>
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => handleRemoveNewFile(file.id)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Area */}
            <div
              className={`edit-file-upload-area ${isDragging ? 'dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="edit-art-files"
                multiple
                onChange={handleFileUpload}
                className="file-input-hidden"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.svg,.ai,.psd,.eps"
              />
              <label htmlFor="edit-art-files" className="edit-file-upload-label">
                <div className="upload-icon">{isDragging ? '📥' : '🎨'}</div>
                <span className="upload-primary">
                  {isDragging ? 'Drop files here' : 'Click to upload'}
                </span>
                <span className="upload-hint">PDF, PNG, JPG, AI, PSD, EPS</span>
              </label>
            </div>

            {/* Notes */}
            <div className="edit-notes-section">
              <div className="edit-form-group">
                <label>Customer Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes visible to everyone..."
                  rows="3"
                />
              </div>
              {canAdmin && (
                <div className="edit-form-group">
                  <label>Internal Notes (Staff Only)</label>
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Private staff notes..."
                    rows="3"
                  />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="job-details-modal-overlay active" onClick={onClose}>
      <div className={`job-details-modal-content ${isEditing ? 'editing-mode' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="job-details-modal-header">
          <div className="header-info">
            <h2>{job.job_number}</h2>
            {!isEditing && (
              <span
                className="status-badge"
                style={{ backgroundColor: STATUS_COLORS[job.status] || '#6b7280' }}
              >
                {job.status}
              </span>
            )}
            {isEditing && <span className="editing-badge">Editing</span>}
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Edit Mode Progress */}
        {isEditing && (
          <div className="edit-wizard-progress">
            {EDIT_STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`edit-wizard-step-indicator ${editStep === step.id ? 'active' : ''} ${editStep > step.id ? 'completed' : ''}`}
                onClick={() => goToEditStep(step.id)}
              >
                <div className="step-dot">
                  {editStep > step.id ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <span>{step.icon}</span>
                  )}
                </div>
                <span className="step-label">{step.title}</span>
                {index < EDIT_STEPS.length - 1 && <div className="step-line" />}
              </div>
            ))}
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Modal Body */}
        <div className="job-details-modal-body">
          {!isEditing ? (
            /* VIEW MODE */
            <>
              {/* Job Header Info */}
              <div className="job-header-info">
                <div className="info-item">
                  <span className="label">Priority</span>
                  <span
                    className="priority-badge"
                    style={{ backgroundColor: PRIORITY_COLORS[job.priority] || '#6b7280' }}
                  >
                    {job.priority}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Due Date</span>
                  <span className="value">{formatDate(job.due_date)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Created</span>
                  <span className="value">{formatDateTime(job.created_at)}</span>
                </div>
              </div>

              {/* Customer Section */}
              <div className="modal-section">
                <h4>Customer Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Customer Name</span>
                    <span className="value">{job.customer_name}</span>
                  </div>
                  {job.school_name && (
                    <div className="info-item">
                      <span className="label">School/Organization</span>
                      <span className="value">{job.school_name}</span>
                    </div>
                  )}
                  {job.contact_name && (
                    <div className="info-item">
                      <span className="label">Contact Name</span>
                      <span className="value">{job.contact_name}</span>
                    </div>
                  )}
                  {job.contact_phone && (
                    <div className="info-item">
                      <span className="label">Phone</span>
                      <span className="value">{job.contact_phone}</span>
                    </div>
                  )}
                  {job.contact_email && (
                    <div className="info-item">
                      <span className="label">Email</span>
                      <span className="value">{job.contact_email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Garments Section */}
              <div className="modal-section">
                <h4>Garments</h4>
                {displayGarments.length > 0 ? (
                  <div className="view-garments-list">
                    {displayGarments.map((garment, index) => (
                      <div key={index} className="view-garment-card">
                        <div className="view-garment-header">
                          <span className="view-garment-type">
                            {garment.garment_type || 'Garment'}
                            {garment.garment_color && ` - ${garment.garment_color}`}
                          </span>
                          <span className="view-garment-qty">
                            {garment.quantity || Object.values(garment.sizes || {}).reduce((a, b) => a + (parseInt(b) || 0), 0)} pcs
                          </span>
                        </div>
                        {garment.sizes && Object.keys(garment.sizes).some(k => garment.sizes[k] > 0) && (
                          <div className="view-garment-sizes">
                            {SIZE_COLUMNS.filter(size => garment.sizes[size] > 0).map(size => (
                              <span key={size} className="view-size-tag">
                                {size}: {garment.sizes[size]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="view-garments-total">
                      <span>Total Pieces:</span>
                      <strong>{job.quantity || 0}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="info-item">
                    <span className="value">No garments specified</span>
                  </div>
                )}
              </div>

              {/* Job Details Section */}
              {(job.job_description || job.total_price) && (
                <div className="modal-section">
                  <h4>Job Details</h4>
                  <div className="info-grid">
                    {job.job_description && (
                      <div className="info-item full-width">
                        <span className="label">Description</span>
                        <span className="value">{job.job_description}</span>
                      </div>
                    )}
                    {job.total_price && (
                      <div className="info-item">
                        <span className="label">Price</span>
                        <span className="value">${parseFloat(job.total_price).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assignment Section */}
              <div className="modal-section">
                <h4>Assignment</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Submitted By</span>
                    <span className="value">{job.submitted_by_name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Assigned To</span>
                    <span className="value">{job.assigned_to_name || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Art Files Section */}
              {job.art_files && job.art_files.length > 0 && (
                <div className="modal-section">
                  <h4>Art Files</h4>
                  <div className="art-files-list">
                    {job.art_files.map((file, index) => (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_API_URL || ''}${file.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="art-file-item"
                      >
                        <span className="file-icon">{getFileIcon(file.mimetype)}</span>
                        <div className="file-details">
                          <span className="file-name">{file.originalName || file.filename}</span>
                          {file.size && <span className="file-size">{formatFileSize(file.size)}</span>}
                        </div>
                        <span className="file-download">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes Section */}
              {(job.notes || job.internal_notes) && (
                <div className="modal-section">
                  <h4>Notes</h4>
                  {job.notes && (
                    <div className="info-item full-width">
                      <span className="label">Customer Notes</span>
                      <span className="value notes-text">{job.notes}</span>
                    </div>
                  )}
                  {job.internal_notes && canAdmin && (
                    <div className="info-item full-width">
                      <span className="label">Internal Notes</span>
                      <span className="value notes-text">{job.internal_notes}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Status Change Section */}
              {canAdmin && job.status !== 'Ready for Pickup' && (
                <div className="modal-section status-change-section">
                  <h4>Update Status</h4>
                  <div className="status-buttons">
                    {workflowStages
                      .filter(stage => stage !== job.status)
                      .map(stage => (
                        <button
                          key={stage}
                          className="status-btn"
                          style={{ borderColor: STATUS_COLORS[stage] }}
                          onClick={() => handleStatusChange(stage)}
                          disabled={loading}
                        >
                          Move to {stage}
                        </button>
                      ))
                    }
                  </div>
                  <div className="status-note-input">
                    <input
                      type="text"
                      placeholder="Add a note for this status change (optional)"
                      value={statusChangeNote}
                      onChange={(e) => setStatusChangeNote(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* History Section */}
              {history.length > 0 && (
                <div className="modal-section">
                  <h4>Status History</h4>
                  <div className="history-list">
                    {history.map((item, index) => (
                      <div key={index} className="history-item">
                        <div className="history-status">
                          {item.previous_status && (
                            <>
                              <span className="old-status">{item.previous_status}</span>
                              <span className="arrow">→</span>
                            </>
                          )}
                          <span
                            className="new-status"
                            style={{ color: STATUS_COLORS[item.new_status] || '#333' }}
                          >
                            {item.new_status}
                          </span>
                        </div>
                        <div className="history-meta">
                          <span className="changed-by">{item.changed_by_name || 'System'}</span>
                          <span className="changed-at">{formatDateTime(item.created_at)}</span>
                        </div>
                        {item.notes && <div className="history-notes">{item.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* EDIT MODE - Wizard */
            renderEditStepContent()
          )}
        </div>

        {/* Modal Footer */}
        <div className="job-details-modal-footer">
          {!isEditing ? (
            <>
              {canAdmin && (
                <>
                  <button
                    className="btn-edit"
                    onClick={handleStartEdit}
                    disabled={loading}
                  >
                    Edit Job
                  </button>
                  {job.is_archived ? (
                    <button
                      className="btn-unarchive"
                      onClick={handleUnarchive}
                      disabled={loading}
                      title="Restore from archive"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="1 4 1 10 7 10"/>
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                      </svg>
                      Restore
                    </button>
                  ) : (
                    <button
                      className="btn-archive"
                      onClick={handleArchive}
                      disabled={loading}
                      title="Archive this job"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="21 8 21 21 3 21 3 8"/>
                        <rect x="1" y="3" width="22" height="5"/>
                        <line x1="10" y1="12" x2="14" y2="12"/>
                      </svg>
                      Archive
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </>
              )}
              <button
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <div className="footer-left">
                {editStep > 1 && (
                  <button
                    className="btn-secondary"
                    onClick={prevEditStep}
                    disabled={loading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                    Back
                  </button>
                )}
              </div>
              <div className="footer-right">
                <button
                  className="btn-cancel"
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Cancel
                </button>
                {editStep < 4 ? (
                  <button
                    className="btn-primary"
                    onClick={nextEditStep}
                    disabled={loading}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                ) : (
                  <button
                    className="btn-save"
                    onClick={handleSaveEdit}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
